import React, { useState, useEffect, useRef } from 'react';
import Tippy from '@tippyjs/react'; // Import Tippy.js
import 'tippy.js/dist/tippy.css'; // Default Tippy styles
import 'tippy.js/animations/scale.css'; // Animation styles

import { FiInfo, FiCopy, FiCheck } from 'react-icons/fi';

interface TooltipContent {
  [key: string]: string; // Each key-value pair is a tooltip row
}

interface DetailedTooltipProps {
  content: TooltipContent | string;
  isCopyable?: boolean;
  placement?: 'top' | 'top-start' | 'top-end' | 'bottom' | 'bottom-start' | 'bottom-end' | 'left' | 'left-start' | 'left-end' | 'right' | 'right-start' | 'right-end';
  cursor?: 'auto' | 'default' | 'none' | 'context-menu' | 'help' | 'pointer' | 'progress' | 'wait' | 'cell' | 'crosshair' | 'text' | 'vertical-text' | 'alias' | 'copy' | 'move' | 'no-drop' | 'not-allowed' | 'grab' | 'grabbing' | 'all-scroll' | 'col-resize' | 'row-resize' | 'n-resize' | 'e-resize' | 's-resize' | 'w-resize' | 'ne-resize' | 'nw-resize' | 'se-resize' | 'sw-resize' | 'ew-resize' | 'ns-resize' | 'nesw-resize' | 'nwse-resize' | 'zoom-in' | 'zoom-out';
  animation?: 'shift-away' | 'shift-away-subtle' | 'shift-toward' | 'fade' | 'scale';
  theme?: string; // Optional theme name
  allowHTML: boolean; // Whether HTML content is allowed
  showIcon?: boolean;
  icon?: JSX.Element;
  children?: React.ReactNode; // Add children prop
  targetEl?: string; // ID of the target element for tooltip
  trigger?: string;
  delay?: [number, number]
  copyText?:string
  tooltipHeading?: string
  onCopy?: () => void;
  interactiveOverride?: boolean;
}

const DetailedTooltip: React.FC<DetailedTooltipProps> = ({
  content,
  isCopyable,
  icon = <FiInfo size={18} color="#000000" />,
  placement = 'top',
  animation = 'fade',
  theme = 'default-tooltip', // Default theme name
  allowHTML,
  targetEl,
  showIcon = true,
  children, // Destructure children
  trigger,
  delay,
  copyText,
  cursor = 'help', // Default cursor
  tooltipHeading = "",
  onCopy,
  interactiveOverride,

}) => {
  const [isCopied, setIsCopied] = useState(false);
  const targetRef = useRef<HTMLElement | null>(null);

  // Handle the copy functionality
  const handleCopy = () => {
    if (isCopyable) {
      if (copyText !== undefined && typeof content === 'string'){
        navigator.clipboard.writeText(copyText);
        setIsCopied(true);
        if (onCopy) {
          onCopy();
        }
      }

      // Reset the copied state after animation ends (e.g., 2 seconds)
      setTimeout(() => setIsCopied(false), 2000);
    }
  };

  // Dynamically generate the tooltip content
  const dynamicContent = (
    <div>
      {/* Copy button placed at the top */}
      {isCopyable && (
        <div
          onClick={handleCopy}
          className={`copy-button ${isCopied ? 'copied' : ''}`}
        >
          {isCopied ? <FiCheck size={16} /> : <FiCopy size={16} />}
          <span style={{ marginLeft: '8px' }}>
            {isCopied ? 'Copied!' : ''}
          </span>
        </div>
      )}

      {/* Render tooltip content below */}
      {typeof content !== 'string' ? 
        Object.entries(content).map(([key, value]) => (
          <div key={key} style={{ marginBottom: '8px' }}>
            <strong>{key}:</strong> <span dangerouslySetInnerHTML={{__html:value}}></span>
          </div>
        )) : <>
          {tooltipHeading && <>
            <span className='sql-query'>{tooltipHeading}:</span>
            <br />
            </>}
          <span dangerouslySetInnerHTML={{__html: content}}></span>
        </>
     }
    </div>
  );

  useEffect(() => {
    // Attach tooltip to the target element using `targetEl` if provided
    if (targetEl) {
      const element = document.getElementById(targetEl);
      if (element) {
        targetRef.current = element; // Set the ref to the target element
      }
    }
  }, [targetEl]);

  return targetRef.current ? (
    <Tippy
      content={dynamicContent}
      placement={placement}
      animation={animation}
      theme={theme}
      allowHTML={allowHTML}
      interactive={interactiveOverride ?? true}
      className="default-tooltip"
      reference={targetRef} // Dynamically assign the tooltip target
      trigger={trigger}
      delay={delay}
    />
  ) : (
    <Tippy
      content={dynamicContent}
      placement={placement}
      animation={animation}
      theme={theme}
      allowHTML={allowHTML}
      interactive={interactiveOverride ?? true}
      className="default-tooltip"
      trigger={trigger}
      delay={delay}
      onMount={(instance) => {
        const tooltipElement = instance.popper;
        if (tooltipElement) {
          tooltipElement.addEventListener('click', (e) => e.preventDefault());    // FP-10749 Block click and mousedown events inside the tooltip to prevent user interactions.
          tooltipElement.addEventListener('mousedown', (e) => e.preventDefault());
        }
      }}
      onHide={(instance) => {
        const tooltipElement = instance.popper;
        if (tooltipElement) {
          tooltipElement.removeEventListener('click', (e) => e.preventDefault());  // FP-10749 Remove event listeners to clean up when the tooltip is hidden.
          tooltipElement.removeEventListener('mousedown', (e) => e.preventDefault());
        }
      }}
    >
      {showIcon ? (
        <span style={{ cursor: cursor, paddingLeft:'5px'}}>{icon ? icon : <FiInfo size={18} color="#000000" />}</span>
      ) : (
        React.isValidElement(children) ? (
          children
        ) : (
          <span>{children}</span>
        )
      )}
    </Tippy>
  );
};

export default DetailedTooltip;
